import * as React from 'react';
import {
    type AnchorHTMLAttributes,
    type ButtonHTMLAttributes,
    type ElementType,
    forwardRef,
    useRef
} from 'react';

import classnames from 'classnames';

import { type SizeType } from '../../../features/config';
import { useMergeRefs } from '../../../hooks/useMergeRefs';
import { useWaveEffect, WaveEffect } from '../../WaveEffect';

import styles from './ButtonBase.module.scss';

export type ButtonColor =
    | 'primary'
    | 'error'
    | 'success'
    | 'warning'
    | 'alert'
    | 'info'
    | 'neutral';
export type ButtonType = 'contained' | 'outlined' | 'ghost';
export type ButtonSize = SizeType;
export type ButtonHTMLType = 'submit' | 'reset' | 'button';

type BaseProps = {
    type?: ButtonType;
    color?: ButtonColor;
    size?: ButtonSize;
    loading?: boolean;
    showBackground?: boolean;
    disabled?: boolean;
    /**
     * Whether the button should take up the full width and height of its container.
     */
    fill?: boolean;
    /**
     * Whether the button should be displayed as a block element, taking up the full width of its container.
     */
    block?: boolean;
    disableWaveEffect?: boolean;
    [key: `data-${string}`]: string;
};

type ButtonElementProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> &
    BaseProps & {
        htmlType?: ButtonHTMLType;
        form?: string;
        href?: undefined;
    };

type AnchorElementProps = BaseProps & {
    htmlType?: undefined;
    form?: undefined;
    href: string;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

export type ButtonBaseProps = ButtonElementProps | AnchorElementProps;

export const ButtonBase = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonBaseProps>(
    (
        {
            className,
            type = 'contained',
            color = 'primary',
            loading = false,
            showBackground = false,
            htmlType = 'button',
            children,
            disabled = false,
            href,
            fill,
            size,
            form,
            disableWaveEffect,
            block,
            ...props
        }: ButtonBaseProps,
        ref
    ) => {
        const Comp = (href ? 'a' : 'button') as ElementType;
        const showAsDisabled = Boolean(disabled || loading);
        const canShowBackground = showBackground && type !== 'contained';

        const waveRef = useRef<HTMLDivElement>(null);
        const buttonRef = useRef<HTMLElement>(null);
        const mergedRefs = useMergeRefs([ref, buttonRef]);

        useWaveEffect(buttonRef, waveRef, {
            colorProp: type === 'contained' ? 'background-color' : 'color'
        });

        const buttonProps = {
            type: htmlType,
            form
        };

        const anchorProps = {
            href
        };

        return (
            <Comp
                className={classnames(
                    styles.base,
                    styles[type],
                    styles[color],
                    size && styles[size],
                    {
                        [styles.showBackground]: canShowBackground,
                        [styles.fill]: fill,
                        [styles.block]: block
                    },
                    className
                )}
                ref={mergedRefs}
                disabled={showAsDisabled}
                aria-busy={Boolean(loading)}
                {...(href ? anchorProps : buttonProps)}
                {...props}
            >
                {!disableWaveEffect && <WaveEffect ref={waveRef} />}
                {children}
            </Comp>
        );
    }
);
ButtonBase.displayName = 'ButtonBase';
